import React, { useState, useEffect, useRef } from 'react';
import { Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateSinglePlanMutation, useGetSinglePlanQuery, useGetCoverageComparisonQuery, useCreatePlanMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { toast } from 'react-toastify';
import { extractErrorMsg } from 'utils/errors';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { coverageTypeEnum, studentStatusEnum, backgroundTypeEnum, coverageTypes, planPaymentMode, planStatusEnum, propertyKindEnum } from 'common/utils/enums';
import { saveCoverageType, selectCoverageType, saveCoverageSelection, selectCoverageSelection, saveUpfrontSplitAmount } from 'redux/features/plans/plansSlice';
import { isWithinDateRange } from 'common/utils/dateRange';
import { getTerm } from 'common/utils/getTerm';
import { leaseTypeEnum } from 'common/utils/enums';

import { isPropertyBulkBilling } from 'common/utils/misc';

// Import step components
import PlanStepPersonalInfo from 'components/plan-purchase/plan-step-personal-info';
import PlanStepCoverageType from 'components/plan-purchase/plan-step-coverage-type';
import PlanStepAdditionalCoverage from 'components/plan-purchase/plan-step-additional-coverage';
import PlanStepLeaseDetails from 'components/plan-purchase/plan-step-lease-details';
import PlanStepSelectProperty from 'components/plan-purchase/plan-step-select-property';
import PlanStepSetPayment from 'components/plan-purchase/plan-step-set-payment';
import PlanPaymentComplete from 'components/plan-purchase/plan-payment-complete';
import StartPurchase from 'components/plan-purchase/start-purchase';
import CancelApplicationModal from 'components/apply-shared/cancel-application-modal';
import EmptyPage from 'common/components/empty-page';
import StudentConfirmationModal from 'components/plans/student-confirmation-modal';

// Import other components
import {ProgressCard} from 'components/apply-shared/progress-card';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ApplyAside,
  ApplyHeader,
  ApplyContainer,
  ApplyMain,
} from 'components/apply-shared/apply-form';
import { FaBuilding, FaShieldAlt, FaCreditCard, FaUser, FaFileAlt } from 'react-icons/fa';
import { FaPerson } from 'react-icons/fa6';
import { Icon, Tag } from '@chakra-ui/react';
import { HiBadgeCheck } from 'react-icons/hi';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import moment from 'moment';

// Define purchase steps
const PurchaseSteps = {
  START: 'START',
  SELECT_PROPERTY: 'SELECT_PROPERTY',
  LEASE_DETAILS: 'LEASE_DETAILS',
  PERSONAL_INFO: 'PERSONAL_INFO',
  COVERAGE_TYPE: 'COVERAGE_TYPE',
  ADDITIONAL_COVERAGE: 'ADDITIONAL_COVERAGE',
  SET_PAYMENT: 'SET_PAYMENT',
  COMPLETE: 'COMPLETE',
};

const PlanPurchase = () => {
  // HOOKS
  const { trackPageLoad, trackLink, trackFormSubmission, trackFormSubmissionError } = useUserTracker();
  const { planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createPlan, { isLoading: createPlanLoading }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: updatePlanLoading }] = useUpdateSinglePlanMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const prevPropertyIdRef = useRef();
  const prevPlanIdRef = useRef();

  // STATE
  const [currentStep, setCurrentStep] = useState(PurchaseSteps.START);
  const [cancelModalState, setCancelModalState] = useState(false);
  const [showStudentConfirmation, setShowStudentConfirmation] = useState(false);

  // Get plan data
  const { data: planData, isLoading: isLoadingPlan, error: planError, refetch: refetchPlan } = useGetSinglePlanQuery(planId, {
    skip: !planId,
    refetchOnMountOrArgChange: true,
  });

  // Get coverage comparison data
  const { data: coverageComparisonData, error: coverageComparisonError, refetch: refetchCoverageComparison } = useGetCoverageComparisonQuery(planId, {
    skip: !planId,
  });

  // Get coverage type from Redux
  const coverageType = useSelector(selectCoverageType);
  const coverageSelection = useSelector(selectCoverageSelection);
  const property = planData?.property || {};
  const isBulkBilling = isPropertyBulkBilling(property);

  // Check if limited coverage is available
  const isLimitedCoverageAvailable = React.useMemo(() => {
    return coverageComparisonData?.hasLimitingDates || false;
  }, [coverageComparisonData?.hasLimitingDates]);

  // Check if cancel anytime is available
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const cancelAnytime = planData?.coverage?.cancelAnytime;
  const cancelAnytimeEligible = React.useMemo(() => {
    const inRange = isWithinDateRange(
      {
        startDate: now,
        endDate: cancelAnytime?.endDate,
      },
      now
    );
    const length =
      getTerm(planData?.lease?.signDate, planData?.lease?.startDate) > 1;

    // If plan is tied to a property, check if cancel anytime is enabled
    let allowed = true;
    if (planData?.property) {
      allowed = planData?.property?.cancelAnytimeEnabled;
    }

    return inRange && length && allowed;
  }, [now, cancelAnytime, planData?.lease?.signDate, planData?.lease?.startDate, planData?.property]);

  // SIDE EFFECTS
  useEffect(() => {
    trackPageLoad('PlanPurchase');
  }, []);

  // Clear coverage selection when the planId changes
  useEffect(() => {
    if (planId && planId !== prevPlanIdRef.current) {
      dispatch(saveCoverageSelection({}));
      dispatch(saveUpfrontSplitAmount(null));
    }
    prevPlanIdRef.current = planId;
  }, [planId, dispatch]);

  // Clear coverage selection when the property changes
  useEffect(() => {
    const currentPropertyId = property?.id;
    if (prevPropertyIdRef.current && currentPropertyId !== prevPropertyIdRef.current) {
      dispatch(saveCoverageSelection({
        [coverageTypes.LEAVE_ANYTIME]: '',
        [coverageTypes.CANCEL_ANYTIME]: '',
      }));
    }
    prevPropertyIdRef.current = currentPropertyId;
  }, [property, dispatch]);

  // LOGIC REDIRECT
  // Make sure users with active, cancelled, or expired plans cannot access the apply page
  const activeStatuses = [
    planStatusEnum.ACTIVE,
    planStatusEnum.ON_GOING_CLAIM,
    planStatusEnum.COMPLETE,
    planStatusEnum.CLOSED,
  ];

  const reviewStatuses = [planStatusEnum.REVIEW];

  const completeStatuses = [
    planStatusEnum.REJECTED,
    planStatusEnum.CANCELLED,
    planStatusEnum.SUSPENDED,
    planStatusEnum.EXPIRED,
  ];

  const notAllowedStatuses = [
    ...activeStatuses,
    ...completeStatuses,
    ...reviewStatuses,
  ];

  useEffect(() => {
    if (notAllowedStatuses.includes(planData?.status)) {
      if (activeStatuses.includes(planData?.status)) {
        navigate(`/dashboard/plans/${planId}`);
      } else if (reviewStatuses.includes(planData?.status)) {
        navigate(`/dashboard/plans/activate/${planId}`);
      } else {
        navigate('/dashboard/plans');
      }
    }
  }, [planData, navigate, notAllowedStatuses, planId, activeStatuses]);

  // STATES
  const hasCompletedPersonalInfo = (plan) => {
    return !!plan?.school?.status;
  };
  const personalInfoReady = hasCompletedPersonalInfo(planData);

  const lease = planData?.lease;
  const coverageReady =
    lease?.address &&
    lease?.monthlyRent &&
    lease?.numBeds &&
    lease?.startDate &&
    lease?.endDate;

  const hasCoverageSelection =
    coverageSelection?.[coverageTypes.LEAVE_ANYTIME] ||
    coverageSelection?.[coverageTypes.CANCEL_ANYTIME];

  const hasPaid =
    planData?.coverage?.leaveAnytime?.payment?.onetime?.stripe?.id ||
    planData?.coverage?.leaveAnytime?.payment?.subscription?.stripe?.id ||
    planData?.coverage?.cancelAnytime?.payment?.stripe?.id;

  const isActivationRequired =
    planData?.status === planStatusEnum.ACTIVATION_REQUIRED;

  // Check for suspicious student situation
  const isSuspiciousStudentSituation = React.useMemo(() => {
    const isStudentProperty = planData?.property?.kind === propertyKindEnum.STUDENT;
    const isNotStudent = planData?.school?.status === studentStatusEnum.NOT_STUDENT;
    return isStudentProperty && isNotStudent;
  }, [planData?.property?.kind, planData?.school?.status]);

  // Set step based on planData
  useEffect(() => {
    if (currentStep !== PurchaseSteps.START) {
      return;
    }  
    
    if (!planId) {
      setCurrentStep(PurchaseSteps.START);
      return;
    }

    if (planData) {
      if (planData.status === planStatusEnum.ACTIVATION_REQUIRED || hasPaid) {
        setCurrentStep(PurchaseSteps.COMPLETE);
      } else if (!planData?.property?.id && !planData?.lease?.address) {
        setCurrentStep(PurchaseSteps.SELECT_PROPERTY);
      } else if (!coverageReady) {
        setCurrentStep(PurchaseSteps.LEASE_DETAILS);
      } else if (!personalInfoReady) {
        setCurrentStep(PurchaseSteps.PERSONAL_INFO);
      } else if (!hasCoverageSelection) {
        setCurrentStep(PurchaseSteps.COVERAGE_TYPE);
      }
    }    
  }, [
    planData,
    coverageReady,
    planId,
    hasCoverageSelection,
    hasPaid,
    personalInfoReady,
  ]);

  const handleStartPurchase = () => {
    const submissionValues = { status: planStatusEnum.PENDING };
    trackFormSubmission(submissionValues);
    createPlan(submissionValues)
      .unwrap()
      .then((data) => {
        dispatch(saveCoverageSelection({}));
        dispatch(saveUpfrontSplitAmount(null));
        navigate(`/dashboard/plans/purchase/${data?.id}`);
      })
      .catch((error) => {
        const errMsg = extractErrorMsg(
          error,
          'We were unable to start your purchase. Please try again.'
        );
        toast.error(errMsg);
        trackFormSubmissionError(submissionValues, errMsg);
      });
  };

  // HANDLERS
  const toggleCancelModal = () => {
    setCancelModalState(!cancelModalState);
  };

  const clearSearchParams = () => {
    searchParams.delete('step');
    setSearchParams(searchParams, { replace: true });
  };

  // Handle student confirmation callbacks
  const handleConfirmNotStudent = () => {
    setShowStudentConfirmation(false);
    // Continue to next step
    if (cancelAnytimeEligible) {
      setCurrentStep(PurchaseSteps.ADDITIONAL_COVERAGE);
    } else {
      setCurrentStep(PurchaseSteps.SET_PAYMENT);
    }
    window.scrollTo(0, 0);
  };

  const handleConfirmStudent = async (values) => {
    try {
      await updatePlan({
        planId: planData.id,
        school: {
          status: studentStatusEnum.CURRENT_STUDENT,
          level: values.schoolLevel,
          graduationDate: values.schoolGraduationDate,
          programEndDate: values.schoolProgramEndDate,
        },
      }).unwrap();
      
      setShowStudentConfirmation(false);
      // Force refetch both plan data and coverage comparison to get updated options
      await Promise.all([
        refetchPlan(),
        refetchCoverageComparison()
      ]);
      // Navigate to coverage type step to let user choose coverage with updated options
      setCurrentStep(PurchaseSteps.COVERAGE_TYPE);
      window.scrollTo(0, 0);
    } catch (error) {
      const errMsg = extractErrorMsg(error, 'Update Failed');
      toast.error(errMsg);
    }
  };



  // Get step number for progress display
  const getStepNumber = (step) => {
    const stepMap = {
      [PurchaseSteps.START]: 0,
      [PurchaseSteps.SELECT_PROPERTY]: 1,
      [PurchaseSteps.LEASE_DETAILS]: 2,
      [PurchaseSteps.PERSONAL_INFO]: 3,
      [PurchaseSteps.COVERAGE_TYPE]: 4,
      [PurchaseSteps.ADDITIONAL_COVERAGE]: 5,
      [PurchaseSteps.SET_PAYMENT]: 6,
      [PurchaseSteps.COMPLETE]: 7,
    };
    return stepMap[step];
  };

  // SIDE EFFECTS
  // Calculate expiration date and days to expire
  const expirationDateString = planData?.expirationDate;
  let formattedExpirationDate = '';
  let daysToExpire = 0;
  if (expirationDateString) {
    formattedExpirationDate = formatDateToHumanDate(expirationDateString);
    daysToExpire = moment.utc(expirationDateString).diff(moment.utc(), 'days');
  }

  // Statuses where we should show expiration
  const pendingStatuses = [
    planStatusEnum.PENDING,
    planStatusEnum.QUOTE,
    planStatusEnum.CREATED,
  ];

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case PurchaseSteps.START:
        return (
          <StartPurchase
            planId={planId}
            onNextClick={() => {
              if (planId) {
                setCurrentStep(PurchaseSteps.PERSONAL_INFO);
              } else {
                handleStartPurchase();
              }
            }}
            isLoading={createPlanLoading}
          />
        );

      case PurchaseSteps.SELECT_PROPERTY:
        return (
          <PlanStepSelectProperty
            planData={planData}
            onNextClick={() => {
              setCurrentStep(PurchaseSteps.LEASE_DETAILS);
              window.scrollTo(0, 0);
            }}
            clearSearchParams={clearSearchParams}
          />
        );

      case PurchaseSteps.LEASE_DETAILS:
        return (
          <PlanStepLeaseDetails
            planData={planData}
            onBackClick={() => {
              setCurrentStep(PurchaseSteps.SELECT_PROPERTY);
              window.scrollTo(0, 0);
            }}
            onNextClick={() => {
              setCurrentStep(PurchaseSteps.PERSONAL_INFO);
              window.scrollTo(0, 0);
            }}
            clearSearchParams={clearSearchParams}
            toggleCancel={toggleCancelModal}
          />
        );

      case PurchaseSteps.PERSONAL_INFO:
        return (
          <PlanStepPersonalInfo
            planData={planData}
            onBackClick={() => {
              setCurrentStep(PurchaseSteps.LEASE_DETAILS);
              window.scrollTo(0, 0);
            }}
            onNextClick={() => {
              setCurrentStep(PurchaseSteps.COVERAGE_TYPE);
              window.scrollTo(0, 0);
            }}
            stepNumber={getStepNumber(PurchaseSteps.PERSONAL_INFO)}
          />
        );

      case PurchaseSteps.COVERAGE_TYPE:
        return (
          <PlanStepCoverageType
            planData={planData}
            onBackClick={() => {
              setCurrentStep(PurchaseSteps.PERSONAL_INFO);
              window.scrollTo(0, 0);
            }}
            onNextClick={() => {
              if (isSuspiciousStudentSituation) {
                setShowStudentConfirmation(true);
              } else {
                if (cancelAnytimeEligible) {
                  setCurrentStep(PurchaseSteps.ADDITIONAL_COVERAGE);
                } else {
                  setCurrentStep(PurchaseSteps.SET_PAYMENT);
                }
                window.scrollTo(0, 0);
              }
            }}
            stepNumber={getStepNumber(currentStep)}
          />
        );

      case PurchaseSteps.ADDITIONAL_COVERAGE:
        return (
          <PlanStepAdditionalCoverage
            planData={planData}
            onBackClick={() => {
              if (isLimitedCoverageAvailable) {
                setCurrentStep(PurchaseSteps.COVERAGE_TYPE);
              } else {
                setCurrentStep(PurchaseSteps.PERSONAL_INFO);
              }
              window.scrollTo(0, 0);
            }}
            onNextClick={() => setCurrentStep(PurchaseSteps.SET_PAYMENT)}
            stepNumber={getStepNumber(currentStep)}
          />
        );

      case PurchaseSteps.SET_PAYMENT:
        return (
          <PlanStepSetPayment
            planData={planData}
            onBackClick={() => {
              if (cancelAnytimeEligible) {
                setCurrentStep(PurchaseSteps.ADDITIONAL_COVERAGE);
              } else {
                setCurrentStep(PurchaseSteps.COVERAGE_TYPE);
              }
              window.scrollTo(0, 0);
            }}
            onNextClick={() => {
              refetchPlan();
              setCurrentStep(PurchaseSteps.COMPLETE);
              window.scrollTo(0, 0);
            }}
          />
        );

      case PurchaseSteps.COMPLETE:
        return (
          <PlanPaymentComplete
            planData={planData}
            onBackClick={() => {
              setCurrentStep(PurchaseSteps.SET_PAYMENT);
              window.scrollTo(0, 0);
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ApplyContainer>
        {/* Header */}
        <ApplyHeader
          onClick={() => {
            navigate('/dashboard/plans');
          }}
        />

        {/* Main Content */}
        {
          isLoadingPlan ? (
            <EmptyPage
              isLoading
              title='Loading Plan'
              subtitle='Please wait while we load your plan.'
            />
          ) : planError ? (
            <EmptyPage
              title='Error Loading Plan'
              subtitle='Please try again.'
            />
          ) : (
            <ApplyMain>
          {/* aside */}
          {![PurchaseSteps.START, PurchaseSteps.COMPLETE].includes(currentStep) && (
            <ApplyAside
              save={() => {
                planData?.status === planStatusEnum.ACTIVATION_REQUIRED
                  ? navigate(`/dashboard/plans?tab=activate`)
                  : navigate('/dashboard/plans?tab=quotes');
              }}
              statusText={expirationDateString}
              saveText={
                planId
                  ? planData?.status === planStatusEnum.ACTIVATION_REQUIRED
                    ? 'Save & Activate Later'
                    : 'Save & Continue Later'
                  : 'Cancel Purchase'
              }
              cancel={toggleCancelModal}
              cancelCondition={pendingStatuses.includes(planData?.status)}
              cancelText='Cancel Purchase'
            >
              {pendingStatuses.includes(planData?.status) ? (
                <div className={`text-sm absolute inset-[0.5rem_0.5rem_0rem_0.5rem] flex flex-row justify-between items-start gap-2 lg:relative lg:inset-0 lg:px-3 lg:py-2 lg:border lg:border-gray-200 lg:rounded-md ${expirationDateString ? '' : 'lg:hidden'}`}>
                  <span className='block lg:hidden mobile:whitespace-nowrap'>
                    Step {getStepNumber(currentStep)} of{' '}
                    {isBulkBilling ? 4 : 5}
                  </span>

                  {expirationDateString && (
                    <span className='text-gray-500 mobile:whitespace-nowrap'>
                      {moment.utc(expirationDateString).isSame(moment.utc(), 'day') ? (
                        <>
                          Offer expires{' '}
                          <span className='font-semibold'>today</span>
                        </>
                      ) : (
                        <>
                          Offer expires in{' '}
                          <span className='font-semibold'>
                            {daysToExpire > 0 ? daysToExpire : 0} days
                          </span>{' '}
                          on{' '}
                          <span className='font-semibold'>
                            {formattedExpirationDate}
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>
              ) : null}

              <ProgressCard
                CardIcon={FaBuilding}
                title='Rental Details'
                step={1}
                onClick={() => setCurrentStep(PurchaseSteps.SELECT_PROPERTY)}
                isBlurred={false}
                isCompleted={planData?.property || planData?.lease?.address}
                isCurrent={currentStep === PurchaseSteps.SELECT_PROPERTY}
              />
              
              <ProgressCard
                CardIcon={FaFileAlt}
                title='Lease Details'
                step={2}
                onClick={() => setCurrentStep(PurchaseSteps.LEASE_DETAILS)}
                isBlurred={!planData?.property && !planData?.lease?.address}
                isCompleted={coverageReady}
                isCurrent={currentStep === PurchaseSteps.LEASE_DETAILS}
              />
              
              <ProgressCard
                CardIcon={FaPerson}
                title='Personal Info'
                step={3}
                onClick={() => setCurrentStep(PurchaseSteps.PERSONAL_INFO)}
                isBlurred={!coverageReady}
                isCompleted={personalInfoReady}
                isCurrent={currentStep === PurchaseSteps.PERSONAL_INFO}
              />
              
              <ProgressCard
                CardIcon={FaShieldAlt}
                title='Choose Coverage'
                step={4}
                onClick={() => setCurrentStep(PurchaseSteps.COVERAGE_TYPE)}
                isBlurred={!personalInfoReady || !coverageReady}
                isCompleted={planData?.coverage?.leaveAnytime?.coverageType}
                isCurrent={currentStep === PurchaseSteps.COVERAGE_TYPE}
              />
              
              
              
              {!isBulkBilling || Object.keys(property).length === 0 ? (
                <>
                  <ProgressCard
                    CardIcon={FaCreditCard}
                    title='Payment'
                    step={5}
                    onClick={() => setCurrentStep(PurchaseSteps.SET_PAYMENT)}
                    isBlurred={!personalInfoReady || !coverageReady || !hasCoverageSelection}
                    isCompleted={hasPaid || isActivationRequired}
                    isCurrent={currentStep === PurchaseSteps.SET_PAYMENT}
                  />
                </>
              ) : (
                <>
                  <div className='w-full h-[1px] bg-gray-200 rounded-full my-2 mobile:hidden' />
                  <Tag
                    size='md'
                    colorScheme='green'
                    className='rounded-full px-3 gap-2 p-2 mobile:hidden'
                  >
                    <Icon as={HiBadgeCheck} boxSize={4} />
                    ReLease Included
                  </Tag>
                </>
              )}
            </ApplyAside>
          )}

          {/* Main content section */}
          <section className='h-full w-full flex flex-col items-center border border-gray-200 divide-y divide-gray-200 rounded-lg mobile:border-none mobile:rounded-none mobile:divide-gray-200'>
            {renderStepContent()}
          </section>
        </ApplyMain>
          )
        }
      </ApplyContainer>

      {/* Cancel Modal */}
      {planId && cancelModalState && (
        <CancelApplicationModal
          planId={planId}
          closeModal={toggleCancelModal}
        />
      )}

      {/* Student Confirmation Modal */}
      <StudentConfirmationModal
        isOpen={showStudentConfirmation}
        onClose={() => setShowStudentConfirmation(false)}
        onConfirmNotStudent={handleConfirmNotStudent}
        onConfirmStudent={handleConfirmStudent}
        planData={planData}
      />
    </>
  );
};

export default PlanPurchase;
